import Image from "next/image";
import Link from "@/components/ui/Link";
import { getReadableImageUrl } from "@/lib/directus";
import { getBlogPostUrl } from "@/lib/blog";
import { useEffect, useRef, useState } from "react";

interface BlogPreviewData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  content_collection?: {
    id: string;
    blog_posts_id: {
      title: string;
      slug: string;
      featured_image: {
        id: string;
        filename_download: string;
      };
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",
  "subheading.text", 
  "subheading.title",
  "content_collection.id",
  "content_collection.blog_posts_id.title",
  "content_collection.blog_posts_id.slug",
  "content_collection.blog_posts_id.featured_image.id",
  "content_collection.blog_posts_id.featured_image.filename_download",
] as const;

interface BlogPreviewProps {
  data?: BlogPreviewData;
}

export default function BlogPreview({ data }: BlogPreviewProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.15, rootMargin: "-50px" }
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, []);

  return (
		<section
			ref={sectionRef}
			id='blog-preview'
			className='w-full py-16 bg-[#f8f8f8]'
		>
			<div className='container'>
				<div
					className={`transition-all duration-700 ease-out ${
						isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
					}`}
				>
					<h2>
						{data?.heading?.title || data?.heading?.text || 'Featured Articles'}
					</h2>
					<div className='w-full h-px bg-[#bcbcbc] mt-8 mb-10' />
				</div>

				<div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-7'>
					{data?.content_collection?.map((post, index) => {
						const title = post.blog_posts_id?.title || ''
						const slug = post.blog_posts_id?.slug || ''
						const imageId = post.blog_posts_id?.featured_image?.id || ''
						const imageFilename =
							post.blog_posts_id?.featured_image?.filename_download || ''
						const blogUrl = getBlogPostUrl(slug)

						return (
							<article
								key={post.id}
								className={`flex flex-col transition-all duration-700 ease-out ${
									isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
								}`}
								style={{
									transitionDelay: isVisible ? `${index * 100}ms` : '0ms',
								}}
							>
								<Link href={blogUrl} className='group block'>
									<div className='relative overflow-hidden bg-[#e5e5e5] aspect-[3/2]'>
										{imageId && imageFilename && (
											<Image
												src={getReadableImageUrl(imageId, imageFilename)}
												alt={title}
												fill
												className='object-cover transition-transform duration-300 group-hover:scale-105'
												quality={90}
												loading='lazy'
												sizes='(min-width: 1280px) 22vw, (min-width: 640px) 45vw, 100vw'
											/>
										)}
									</div>
								</Link>

								<h3 className='mt-5 text-[20px] leading-[1.35] uppercase font-aileron font-semibold text-[#1f1f1f] md:h-24'>
									{title}
								</h3>
								<div className='w-full'>
									<div className='mt-6 text-left w-full'>
										<Link
											href={blogUrl}
											className='btn w-full lg:w-auto text-center'
										>
											Learn More
										</Link>
									</div>
								</div>
							</article>
						)
					})}
				</div>
			</div>
		</section>
	)
}
